import { useEffect, useRef } from 'react';
import { SYNC_CONFIG } from '@/config/sync';
import { handleBackgroundError } from '@/lib/error-handler';
import { syncAll } from '@/lib/sync/orchestrator';
import { useAuthStore } from '@/stores';
import { useNetworkStatus } from './useNetworkStatus';

/**
 * Hook to automatically sync all entities when network comes back online
 * Also processes sync queue when coming back online
 */
export function useAutoSync(): void {
  const { online } = useNetworkStatus();
  const userId = useAuthStore((state) => state.userId);
  const wasOfflineRef = useRef(!online);
  const lastSyncRef = useRef<number>(0);

  useEffect(() => {
    // Only sync if:
    // 1. We just came back online (was offline, now online)
    // 2. User is authenticated
    // 3. We haven't synced in the last debounce period (prevents rapid successive syncs)
    if (
      wasOfflineRef.current &&
      online &&
      userId &&
      Date.now() - lastSyncRef.current > SYNC_CONFIG.autoSyncDebounceMs
    ) {
      lastSyncRef.current = Date.now();

      // Sync all entities
      void (async () => {
        try {
          // Get all session IDs from local metadata
          const { db } = await import('@/db/client');
          const sessionMetadata = await db.sessionMetadata
            .filter((meta) => !meta.deletedAt)
            .toArray();
          const sessionIds = sessionMetadata.map((meta) => meta.id);

          await syncAll(userId, sessionIds);
        } catch (error) {
          handleBackgroundError(error, { operation: 'autoSync', userId }, 'warn');
        }
      })();
    }
    wasOfflineRef.current = !online;
  }, [online, userId]);
}
